import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { CrmEtapasKanbanService } from '../../../shared/services/apis/vendas/crm-etapas-kanban.service';
import { PerfilService } from '../../../shared/services/apis/usuario/perfil.service';

/**
 * CA24 — quem é notificado ao mover um card para uma etapa.
 *
 * Perfil → usuário vive na API de Usuário, que o CRM não consulta: o front expande e manda
 * os ids na própria movimentação, mesmo arranjo do AutomacaoNotificacaoHelper de Compras.
 *
 * Vive num serviço porque o board e a ficha do card movem cards pelos dois caminhos, e a
 * resolução custa duas chamadas — duplicar a lógica sairia caro nos dois sentidos.
 */
@Injectable({ providedIn: 'root' })
export class EnvolvidosEtapaService {
  /** Cache por etapa: mover vários cards para a mesma coluna não repete o trabalho. */
  private cache = new Map<number, number[]>();

  constructor(
    private crmEtapasKanbanService: CrmEtapasKanbanService,
    private perfilService: PerfilService
  ) {}

  /**
   * Ids de usuário envolvidos na etapa. Notificar é acessório: qualquer falha devolve lista
   * vazia para a movimentação seguir — ninguém perde o card porque o sino falhou.
   */
  public getEnvolvidos(idEtapaKanban: number): Observable<number[]> {
    const emCache = this.cache.get(idEtapaKanban);
    if (emCache) return of(emCache);

    return this.crmEtapasKanbanService.getConfiguracao(idEtapaKanban).pipe(
      switchMap(config => {
        if (config.isError) return of([] as number[]);

        const idsPerfil = [...new Set((config.permissoes ?? []).map(p => p.idPerfil))];
        if (!idsPerfil.length) return of([] as number[]);

        return this.perfilService.getUsuariosPorPerfil(idsPerfil).pipe(
          map(resposta =>
            resposta.isError ? [] : [...new Set((resposta.lista ?? []).map(v => v.idUsuario))]
          ),
          catchError(() => of([] as number[]))
        );
      }),
      catchError(() => of([] as number[])),
      tap(ids => this.cache.set(idEtapaKanban, ids))
    );
  }

  /** A configuração da etapa mudou: descarta o cache para não notificar quem saiu. */
  public invalidar(idEtapaKanban?: number): void {
    if (idEtapaKanban == null) this.cache.clear();
    else this.cache.delete(idEtapaKanban);
  }
}
